import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiStar } from "react-icons/fi";
import { featuredServices, mainCategories, smallCategories } from "./homePreviewData.js";
import ScrollReveal from "./ScrollReveal.jsx";

const MotionLink = motion.create(Link);

// Explicit placement on a 4-col x 4-row grid at lg+ so every cell is filled with no gaps.
const bentoSpans = [
  "sm:col-span-2 lg:col-start-1 lg:col-span-2 lg:row-start-1 lg:row-span-2", // Search Banquet (featured)
  "lg:col-start-3 lg:col-span-1 lg:row-start-1 lg:row-span-1", // Birthday Decoration
  "lg:col-start-4 lg:col-span-1 lg:row-start-1 lg:row-span-2", // First Birthday (tall)
  "lg:col-start-3 lg:col-span-1 lg:row-start-2 lg:row-span-1", // Baby Shower
  "sm:col-span-2 lg:col-start-1 lg:col-span-2 lg:row-start-3 lg:row-span-1", // Welcome Baby (wide)
  "lg:col-start-3 lg:col-span-1 lg:row-start-3 lg:row-span-1", // Wedding Events
  "lg:col-start-3 lg:col-span-1 lg:row-start-4 lg:row-span-1", // Cake Table Setup
  "lg:col-start-4 lg:col-span-1 lg:row-start-3 lg:row-span-2", // Return Gifts (tall)
  "sm:col-span-2 lg:col-start-1 lg:col-span-2 lg:row-start-4 lg:row-span-1", // Birthday Activities (wide)
];

function CategoryCard({ card, span }) {
  return (
    <MotionLink
      to={card.link}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-3xl border border-zoom-emerald/10 bg-white shadow-hp-lg transition-shadow duration-300 hover:shadow-hp-glow ${span}`}
    >
      {/* Image zone — image only, heading sits in its own solid chip */}
      <div className="relative aspect-[4/3] flex-1 overflow-hidden bg-zoom-emeraldDeep lg:aspect-auto lg:h-full">
        <img
          src={card.image}
          alt={card.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.08]"
        />
        <div className="absolute left-3 top-3 rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zoom-goldSoft backdrop-blur">
          DecorZoom
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="inline-block rounded-xl bg-zoom-emeraldDeep/90 px-3 py-1.5 font-display text-lg font-bold leading-tight text-white shadow-hp-sm backdrop-blur-sm sm:text-xl">
            {card.title}
          </h3>
        </div>
      </div>
    </MotionLink>
  );
}

export default function HomePreviewServices() {
  return (
    <section id="preview-services" className="bg-zoom-warm py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <ScrollReveal className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-zoom-gold">Plan your celebration</p>
            <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-zoom-emeraldDeep sm:text-[44px] sm:leading-tight">
              Beautiful event services for birthdays, baby showers, weddings and family functions.
            </h2>
            <p className="mt-4 text-base font-normal leading-8 text-zoom-charcoal/66">
              Book decoration and banquet in one place with clear options, verified support, and quick guidance on WhatsApp.
            </p>
          </div>
          <Link
            to="/banquets"
            className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-zoom-emeraldDeep px-6 py-3 text-sm font-semibold text-white shadow-hp-emerald transition hover:-translate-y-0.5 hover:bg-zoom-emerald"
          >
            Search Banquets <FiArrowRight aria-hidden="true" />
          </Link>
        </ScrollReveal>

        <ScrollReveal delay={0.1} className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[260px]">
          {mainCategories.map((card, index) => (
            <CategoryCard key={card.title} card={card} span={bentoSpans[index] || ""} />
          ))}
        </ScrollReveal>

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {featuredServices.map((service, index) => (
            <ScrollReveal
              key={service.title}
              delay={index * 0.08}
              className="rounded-3xl border border-zoom-emerald/10 bg-white/86 p-6 shadow-hp-md backdrop-blur transition duration-300 hover:-translate-y-1 hover:shadow-hp-glow"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-zoom-emeraldDeep text-zoom-goldSoft">
                <FiStar aria-hidden="true" />
              </span>
              <h3 className="mt-5 text-xl font-semibold leading-tight text-zoom-emeraldDeep">{service.title}</h3>
              <p className="mt-3 text-sm font-normal leading-7 text-zoom-charcoal/64">{service.detail}</p>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal
          delay={0.15}
          className="mt-10 overflow-hidden rounded-3xl border border-zoom-gold/40 bg-[linear-gradient(135deg,#ffffff_0%,#F5F0E8_54%,#eefcf2_100%)] p-6 shadow-hp-md sm:p-8"
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-zoom-gold">More Services</p>
              <h3 className="font-display mt-3 text-2xl font-bold text-zoom-emeraldDeep">Quick picks for extra event requirements.</h3>
            </div>
            <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 sm:grid sm:grid-cols-3 sm:overflow-visible sm:pb-0 lg:max-w-3xl lg:grid-cols-4">
              {smallCategories.map((item) => (
                <Link
                  key={item.label}
                  to={item.link}
                  className="min-h-[48px] min-w-[154px] rounded-full border border-white bg-white/92 px-4 py-3 text-center text-sm font-semibold text-zoom-emeraldDeep shadow-hp-xs transition hover:-translate-y-0.5 hover:border-zoom-gold hover:bg-white sm:min-w-0"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
